"use client";

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
interface LeaderboardHeaderControlMeasurements {
  readonly rowWidth: number;
  readonly viewModesWidth: number;
  readonly sortTabsWidth: number;
  readonly sortDropdownWidth: number;
  readonly curationTabsWidth: number;
  readonly curationDropdownWidth: number;
  readonly actionsFullWidth: number;
  readonly actionsIconWidth: number;
}
interface UseLeaderboardHeaderControlMeasurementsInput {
  readonly showCurationGroupSelect: boolean;
  readonly measureAgainstHeaderRow: boolean;
  readonly remeasureKey: string;
}
interface UseLeaderboardHeaderControlMeasurementsResult {
  readonly headerRowRef: RefObject<HTMLDivElement | null>;
  readonly controlsRowRef: RefObject<HTMLDivElement | null>;
  readonly viewModeTabsRef: RefObject<HTMLDivElement | null>;
  readonly sortTabsProbeRef: RefObject<HTMLDivElement | null>;
  readonly sortDropdownProbeRef: RefObject<HTMLDivElement | null>;
  readonly curationTabsProbeRef: RefObject<HTMLDivElement | null>;
  readonly curationDropdownProbeRef: RefObject<HTMLDivElement | null>;
  readonly actionsFullProbeRef: RefObject<HTMLDivElement | null>;
  readonly actionsIconProbeRef: RefObject<HTMLDivElement | null>;
  readonly measurements: LeaderboardHeaderControlMeasurements;
}
const INITIAL_MEASUREMENTS: LeaderboardHeaderControlMeasurements = {
  rowWidth: 0,
  viewModesWidth: 0,
  sortTabsWidth: 0,
  sortDropdownWidth: 0,
  curationTabsWidth: 0,
  curationDropdownWidth: 0,
  actionsFullWidth: 0,
  actionsIconWidth: 0
};
const areMeasurementsEqual = (current: LeaderboardHeaderControlMeasurements, next: LeaderboardHeaderControlMeasurements): boolean => current.rowWidth === next.rowWidth && current.viewModesWidth === next.viewModesWidth && current.sortTabsWidth === next.sortTabsWidth && current.sortDropdownWidth === next.sortDropdownWidth && current.curationTabsWidth === next.curationTabsWidth && current.curationDropdownWidth === next.curationDropdownWidth && current.actionsFullWidth === next.actionsFullWidth && current.actionsIconWidth === next.actionsIconWidth;
export function useLeaderboardHeaderControlMeasurements(t0) {
  const $ = _c(12);
  const {
    showCurationGroupSelect,
    measureAgainstHeaderRow,
    remeasureKey
  } = t0;
  const headerRowRef = useRef(null);
  const controlsRowRef = useRef(null);
  const viewModeTabsRef = useRef(null);
  const sortTabsProbeRef = useRef(null);
  const sortDropdownProbeRef = useRef(null);
  const curationTabsProbeRef = useRef(null);
  const curationDropdownProbeRef = useRef(null);
  const actionsFullProbeRef = useRef(null);
  const actionsIconProbeRef = useRef(null);
  const [measurements, setMeasurements] = useState(INITIAL_MEASUREMENTS);
  let t1;
  if ($[0] !== measureAgainstHeaderRow || $[1] !== showCurationGroupSelect) {
    t1 = () => {
      const rowWidth = measureAgainstHeaderRow ? headerRowRef.current?.clientWidth ?? 0 : controlsRowRef.current?.clientWidth ?? 0;
      const nextMeasurements = {
        rowWidth,
        viewModesWidth: viewModeTabsRef.current?.offsetWidth ?? 0,
        sortTabsWidth: sortTabsProbeRef.current?.offsetWidth ?? 0,
        sortDropdownWidth: sortDropdownProbeRef.current?.offsetWidth ?? 0,
        curationTabsWidth: showCurationGroupSelect ? curationTabsProbeRef.current?.offsetWidth ?? 0 : 0,
        curationDropdownWidth: showCurationGroupSelect ? curationDropdownProbeRef.current?.offsetWidth ?? 0 : 0,
        actionsFullWidth: actionsFullProbeRef.current?.offsetWidth ?? 0,
        actionsIconWidth: actionsIconProbeRef.current?.offsetWidth ?? 0
      };
      setMeasurements(currentMeasurements => areMeasurementsEqual(currentMeasurements, nextMeasurements) ? currentMeasurements : nextMeasurements);
    };
    $[0] = measureAgainstHeaderRow;
    $[1] = showCurationGroupSelect;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const measureNow = t1;
  let t2;
  if ($[3] !== measureNow || $[4] !== showCurationGroupSelect) {
    t2 = () => {
      let frameId = null;
      let timeoutId = null;
      let destroyed = false;
      const cancelScheduledMeasure = () => {
        if (frameId !== null && typeof globalThis.window.cancelAnimationFrame === "function") {
          globalThis.window.cancelAnimationFrame(frameId);
        }
        frameId = null;
        if (timeoutId !== null && typeof globalThis.clearTimeout === "function") {
          globalThis.clearTimeout(timeoutId);
        }
        timeoutId = null;
      };
      const scheduleMeasure = () => {
        cancelScheduledMeasure();
        if (typeof globalThis.window.requestAnimationFrame === "function") {
          frameId = globalThis.window.requestAnimationFrame(() => {
            frameId = null;
            if (!destroyed) {
              measureNow();
            }
          });
          return;
        }
        timeoutId = globalThis.setTimeout(() => {
          timeoutId = null;
          if (!destroyed) {
            measureNow();
          }
        }, 0);
      };
      scheduleMeasure();
      const observedElements = [headerRowRef.current, controlsRowRef.current, viewModeTabsRef.current, sortTabsProbeRef.current, sortDropdownProbeRef.current, showCurationGroupSelect ? curationTabsProbeRef.current : null, showCurationGroupSelect ? curationDropdownProbeRef.current : null, actionsFullProbeRef.current, actionsIconProbeRef.current].filter(_temp);
      if (typeof ResizeObserver === "undefined") {
        globalThis.window.addEventListener("resize", scheduleMeasure);
        return () => {
          destroyed = true;
          cancelScheduledMeasure();
          globalThis.window.removeEventListener("resize", scheduleMeasure);
        };
      }
      const observer = new ResizeObserver(() => {
        scheduleMeasure();
      });
      observedElements.forEach(element_0 => observer.observe(element_0));
      return () => {
        destroyed = true;
        cancelScheduledMeasure();
        observer.disconnect();
      };
    };
    $[3] = measureNow;
    $[4] = showCurationGroupSelect;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== measureNow || $[7] !== remeasureKey || $[8] !== showCurationGroupSelect) {
    t3 = [measureNow, remeasureKey, showCurationGroupSelect];
    $[6] = measureNow;
    $[7] = remeasureKey;
    $[8] = showCurationGroupSelect;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== measurements) {
    t4 = {
      headerRowRef,
      controlsRowRef,
      viewModeTabsRef,
      sortTabsProbeRef,
      sortDropdownProbeRef,
      curationTabsProbeRef,
      curationDropdownProbeRef,
      actionsFullProbeRef,
      actionsIconProbeRef,
      measurements
    };
    $[10] = measurements;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function _temp(element) {
  return Boolean(element);
}